import { Box, Button, Icon, Paper, Tab, Typography } from "@material-ui/core";
import TabContext from "@material-ui/lab/TabContext";
import TabList from "@material-ui/lab/TabList";
import TabPanel from "@material-ui/lab/TabPanel";
import React, { useEffect, useState } from "react";
import ImageLoader from "react-load-image";
import { connect } from "react-redux";
import { Link, NavLink, useParams, useRouteMatch } from "react-router-dom";
import defaultProfile from "../../../../assets/group.png";
import AppBreadCrumb from "../../../../components/AppBreadCrumb/AppBreadCrumb";
import ConfirmationDialog from "../../../../utilities/components/ConfirmationDialog/ConfirmationDialog";
import Preloader from "../../../../utilities/components/Preloader/Preloader";
import { APP_URLS, PAGE_SIZE, ROLES } from "../../../../utilities/constants";
import { getUserInfo } from "../../../../utilities/storage";
import OrganizationAdministrators from "../../../Administrators/components/OrganizationAdmins/OrganizationAdminis";
import OrganizationDepartments from "../../../Departments/components/OrganizationDepartments/OrganizationDepartments";
import { getDeptsRequest } from "../../../Departments/DepartmentsApiActions";
import OrganizationDoctors from "../../../Doctors/components/OrganizationDoctors/OrganizationDoctors";
import { handleTabChange } from "../../OrganisationsActions";
import {
  getOrgByIdRequest,
  toggleActivationRequest,
} from "../../OrganisationsApiActions";
import { useStyles } from "./styles";
import ClinicTiming from "./ClinicTiming";

/**
 * function to get message for activation dialog
 * @param org: org details
 * @param classes: classes from css
 * @returns {JSX.Element}
 * @constructor
 */
const ToggleActivationDialogMessage = ({ org, classes }) => {
  return (
    <>
      {org && (
        <Box>
          Are you sure you want to {org.active ? "deactivate" : "activate"} the{" "}
          <span className={classes.dialogMessageLabel}>{org.name}</span>?
        </Box>
      )}
    </>
  );
};

/**
 * function to render organization profile
 * @param history: history from router
 * @param location: location from router
 * @param getOrgById: function to get organization by id
 * @param org: organizations details
 * @param handleTabChange: function to handle tab change
 * @param tabValue: tab value
 * @param toggleActivation: function to toggle organization activation
 * @param getAllDepts: function to get departments
 * @returns {JSX.Element}
 * @constructor
 */
const OrganisationProfile = ({
  history,
  location,
  getOrgById,
  org,
  handleTabChange,
  tabValue,
  toggleActivation,
  getAllDepts,
}) => {
  const classes = useStyles();
  const role = getUserInfo().Roles[0].name;
  const params = useParams();
  const match = useRouteMatch();
  const [
    openToggleActivationConfirmationDialog,
    setOpenToggleActivationConfirmationDialog,
  ] = useState(false);
  const currentUrlPath = match.path.split(":")[0];
  const [headOrgId, setHeadOrgId] = useState(null);

  useEffect(() => {
    if (params.id) {
      getOrgById(params.id)
        .then((res) => {
          setHeadOrgId(res.HeadOrganizationId);
        })
        .catch((err) => {
          console.error("API failed:", err);
        });
    }
  }, [params.id, getOrgById]);

  const breadCrumbDataForOrgProfilePage = [
    {
      name: `${org ? `${org.name}` : ""}`,
      path: `${APP_URLS.ORGANIZATIONS}/${params.id}`,
    },
  ];

  /**
   * function to get breadcrumb data for current url
   */
  const getBreadcrumbData = () => {
    return breadCrumbDataForOrgProfilePage;
  };

  /**
   * function to handle tab change
   * @param e: event from button click
   * @param newValue: updated value
   */
  const handleChange = (e, newValue) => {
    handleTabChange(newValue);
  };

  /**
   * function to toggle activation
   */
  const onToggleActivation = () => {
    const data = {
      active: !org.active,
    };

    toggleActivation(params.id, data).then((res) => {
      setOpenToggleActivationConfirmationDialog(false);
      getAllDepts(params.id, {
        page: 0,
        rowsPerPage: PAGE_SIZE,
        searchData: {},
      }).then(() => {});
    });
  };

  const isRoleClinicOwner =
    role === ROLES.ROLE_CLINIC_OWNER ||
    role === ROLES.ROLE_ORG_ADMIN ||
    role === ROLES.ROLE_SUPER_ADMIN;
  return (
    <Box className={classes.outerContainer}>
      <AppBreadCrumb>
        {getBreadcrumbData(currentUrlPath).map((crumb, index) => (
          <Box
            key={index}
            component={NavLink}
            exact
            to={{
              pathname: crumb.path,
              state: {},
            }}
            className={classes.breadcrumbItem}
            activeClassName={classes.activeBreadcrumbItem}
          >
            {crumb.icon && (
              <Icon className={classes.breadcrumpIcon}>{crumb.icon}</Icon>
            )}
            <Box className={classes.breadcrumpLabel}>{crumb.name}</Box>
          </Box>
        ))}
      </AppBreadCrumb>

      <Box mt={3}>
        {org && (
          <>
            <Box>
              <Box component={Paper} className={classes.profileContainer}>
                <Box className={classes.profileContentContainer}>
                  <Box>
                    <ImageLoader
                      src={org.imageUrl ? org.imageUrl : defaultProfile}
                    >
                      <img
                        alt="profile"
                        className={`${classes.profileImage} ${
                          org.imageUrl ? "" : classes.imageBorder
                        }`}
                      />
                      <div className={classes.profileImageError}>{org.id}</div>
                      <Preloader
                        className={`${classes.profileImage} ${
                          org.imageUrl ? "" : classes.imageBorder
                        }`}
                      />
                    </ImageLoader>
                  </Box>
                  <Box className={classes.profileTextContainer}>
                    <Typography
                      classes={{
                        root: classes.orgNameText,
                      }}
                    >
                      {org.name}
                    </Typography>
                    <Typography
                      classes={{
                        root: classes.orgDescText,
                      }}
                    >
                      {org.desc}
                    </Typography>

                    <Box className={classes.editDetailsButtonContainer}>
                      <Button
                        variant="contained"
                        color="primary"
                        disableElevation={true}
                        startIcon={<Icon>edit</Icon>}
                        component={Link}
                        to={{
                          pathname: `${match.path.split(":")[0]}edit/${
                            params.id
                          }`,
                        }}
                        classes={{ root: classes.profileButton }}
                      >
                        Edit Details
                      </Button>
                    </Box>

                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        disableElevation={true}
                        startIcon={
                          org.active === false ? (
                            <Icon>check_circle_outline</Icon>
                          ) : (
                            <Icon>block</Icon>
                          )
                        }
                        onClick={() =>
                          setOpenToggleActivationConfirmationDialog(true)
                        }
                        classes={{
                          root: `${classes.profileButton} ${
                            org.active
                              ? classes.blockButton
                              : classes.activeButton
                          }`,
                        }}
                      >
                        {org.active === false ? "Activate" : "Deactivate"}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box component={Paper} className={classes.tabsContainer}>
                <TabContext value={tabValue}>
                  <TabList
                    onChange={handleChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                  >
                    {isRoleClinicOwner && (
                      <Tab label="Availability" value="1" />
                    )}
                    <Tab
                      label="Departments"
                      value={isRoleClinicOwner ? "2" : "1"}
                    />
                    <Tab label="Staff" value={isRoleClinicOwner ? "3" : "2"} />
                    <Tab
                      label="Administrators"
                      value={isRoleClinicOwner ? "4" : "3"}
                    />
                  </TabList>
                  {isRoleClinicOwner && (
                    <TabPanel
                      value="1"
                      classes={{ root: classes.tabPanelContainer }}
                    >
                      <Box
                        component={Paper}
                        className={classes.profileContainer}
                        sx={{ mt: 2 }}
                      >
                        <Box className={classes.profileContentContainer}>
                          <ClinicTiming clinicId={params.id} mode="clinic" />
                        </Box>
                      </Box>
                    </TabPanel>
                  )}
                  <TabPanel
                    value={isRoleClinicOwner ? "2" : "1"}
                    classes={{ root: classes.tabPanelContainer }}
                  >
                    <OrganizationDepartments
                      history={history}
                      orgIdHistory={org.id}
                      orgNameHistory={org.name}
                      orgTypeIdHistory={org.typeId}
                    />
                  </TabPanel>
                  <TabPanel
                    value={isRoleClinicOwner ? "3" : "2"}
                    classes={{ root: classes.tabPanelContainer }}
                  >
                    <OrganizationDoctors
                      orgIdHistory={org.id}
                      orgNameHistory={org.name}
                    />
                  </TabPanel>
                  <TabPanel
                    value={isRoleClinicOwner ? "4" : "3"}
                    classes={{ root: classes.tabPanelContainer }}
                  >
                    <OrganizationAdministrators
                      history={history}
                      orgIdHistory={org.id}
                      orgNameHistory={org.name}
                    />
                  </TabPanel>
                </TabContext>
              </Box>
            </Box>
          </>
        )}

        {org && (
          <Box>
            <ConfirmationDialog
              title={org.active ? "Deactivate" : "Activate"}
              message={
                <ToggleActivationDialogMessage org={org} classes={classes} />
              }
              open={openToggleActivationConfirmationDialog}
              setOpen={setOpenToggleActivationConfirmationDialog}
              onConfirm={onToggleActivation}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    org: state.organisations.org,
    tabValue: state.organisations.tabValue,
  };
};

const mapDispatchToProp = (dispatch) => {
  return {
    getOrgById: (id) => {
      return dispatch(getOrgByIdRequest(id)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    handleTabChange: (value) => {
      return dispatch(handleTabChange(value));
    },
    toggleActivation: (orgId, data) => {
      return dispatch(toggleActivationRequest(orgId, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getAllDepts: (orgId, data) => {
      return dispatch(getDeptsRequest(orgId, data)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProp)(OrganisationProfile);
